import { memo, useEffect, useCallback, useState } from 'react'

import * as Styled from './styled'
import { removeExtraSpaces } from './utils'
import { TTextWarningTypes } from 'common-components/CharacterCountIndicator/types'
import { T } from 'consts/translationKeys'
import { translate } from 'utils/translations'

const TTS_TEXT_AREA_MAX_HEIGHT = 300
const TTS_TEXT_AREA_ACTIVE_MIN_HEIGHT = 84
const TTS_TEXT_AREA_MAX_CHARACTERS = 900
const CHARACTERS_ERROR_STATE = 'error'

const getTextWarningErrorState = (
  count: number,
  length: number
): TTextWarningTypes | undefined => (length >= count ? 'error' : undefined)

interface ITextAreaProps {
  isActive: boolean
  textValue: string
  setTextValue: (value: string) => void
  mutableTtsTextValue: string // this is the value from mutableTtsState (e.g. user's input in tts items (sidebar))
  initialTtsTextValue: string // this is the value from initialTtsState (e.g. initial request response from audio assetIds)
  handleTextBlurAction?: (newValue: string) => void
  handleTextChangeAction?: (newValue: string) => void
  isDefaultStateFocused: boolean
  maxHeight?: number
  minHeight?: number
  inputFixHeight?: number
  // Item-state flags used only for styling. The TTS tab passes real values from
  // its item state; other consumers (e.g. the tone instruction) leave them off.
  isLoading?: boolean
  isGenerated?: boolean
  isDraft?: boolean
}

const TtsTextArea: React.FC<ITextAreaProps> = ({
  isActive,
  textValue,
  mutableTtsTextValue,
  initialTtsTextValue,
  setTextValue,
  handleTextBlurAction,
  handleTextChangeAction,
  isDefaultStateFocused,
  maxHeight = TTS_TEXT_AREA_MAX_HEIGHT,
  minHeight = TTS_TEXT_AREA_ACTIVE_MIN_HEIGHT,
  inputFixHeight,
  isLoading = false,
  isGenerated = false,
  isDraft = false
}: ITextAreaProps) => {
  const [isFocused, setIsFocused] = useState(isDefaultStateFocused)
  const currentTextLength = textValue.length

  const textWarningState = getTextWarningErrorState(
    TTS_TEXT_AREA_MAX_CHARACTERS,
    currentTextLength
  )
  const isError = textWarningState === CHARACTERS_ERROR_STATE
  const errorOutLine = isFocused && isError

  //this is used to fix the height of the text area when it is not focused and not active
  //it is used to hide scroll and overflow
  const textAreaFixHeight =
    !isFocused && inputFixHeight ? inputFixHeight : undefined

  const onTextValueChange = useCallback(
    (value: string) => {
      handleTextChangeAction?.(value)
      setTextValue(value)
    },
    [handleTextChangeAction, setTextValue]
  )

  useEffect(() => {
    setTextValue(mutableTtsTextValue)
  }, [mutableTtsTextValue, setTextValue])

  const handleFocus = useCallback(() => {
    if (!isFocused) {
      setIsFocused(true)
    }
  }, [isFocused])

  const handleScroll = (event: React.UIEvent<HTMLTextAreaElement>) => {
    event.stopPropagation()
  }

  const handleTextBlur = useCallback(() => {
    setIsFocused(false)

    const newValue = removeExtraSpaces(textValue)
    const isEmpty = newValue.length === 0

    if (isEmpty && !isDraft) {
      setTextValue(initialTtsTextValue)
      return
    }
    if (newValue !== textValue) {
      setTextValue(newValue)
    }
    if (newValue !== removeExtraSpaces(mutableTtsTextValue)) {
      handleTextBlurAction?.(newValue)
    }
  }, [
    mutableTtsTextValue,
    textValue,
    isDraft,
    handleTextBlurAction,
    setTextValue,
    initialTtsTextValue
  ])

  // Delay blur handling until after the next paint to prevent UI layout shifts from interfering with button clicks
  const handleTextBlurDelayed = useCallback(
    (event: React.FocusEvent<HTMLTextAreaElement>) => {
      event.stopPropagation()
      setTimeout(() => {
        handleTextBlur()
      }, 100)
    },
    [handleTextBlur]
  )

  return (
    <>
      <Styled.TextToSpeechTextArea $isLoading={isLoading}>
        <Styled.TextArea
          placeholder={isFocused ? '' : translate(T.TYPE_YOUR_TEXT)}
          maxLength={TTS_TEXT_AREA_MAX_CHARACTERS}
          maxHeight={maxHeight}
          value={textValue}
          shouldFocus={isFocused}
          $isFocused={isFocused}
          $isError={isError}
          $isActive={isActive}
          $isGenerated={isGenerated}
          $minHeight={minHeight}
          hasErrorOutline={errorOutLine}
          onBlur={handleTextBlurDelayed}
          onFocus={handleFocus}
          onChange={onTextValueChange}
          onScroll={handleScroll}
          isDefaultStateFocused={isDefaultStateFocused}
          lockedHeight={textAreaFixHeight}
        />
        {!isFocused && (
          <Styled.TextAreaPseudoBlock
            $isGenerated={isGenerated}
            $isActive={isActive}
          />
        )}
      </Styled.TextToSpeechTextArea>
      <Styled.TextCharacterCountIndicator
        currentTextLength={currentTextLength}
        maxCount={TTS_TEXT_AREA_MAX_CHARACTERS}
        textWarningState={textWarningState}
        hasText={false}
        $isActive={isActive}
        $isFocused={isFocused}
      />
    </>
  )
}

export default memo(TtsTextArea)
